import { useCallback, useState, useRef } from 'react';
import { useDebounce } from 'hooks/useDebounce';
import { hasValue } from 'swipeclock-frontend';
import { useLoginContext, FORM_IDS } from 'providers/LoginProvider';

export const useLoginInputHandler = ({ ids, validationCallback }) => {
  // Integrate LoginProvider to access global state and actions.
  const {
    state,
    actions,
    meta: { pages },
  } = useLoginContext();
  const validationRef = useRef({});
  const currentPageRef = useRef(state.currentPage);

  // Initialize errors from global state.
  const { errors } = state;

  // Convert error messages into booleans for each input to track their validity.
  // We use this to determine if the input should be dirty or not.
  const errorBooleans = Object.keys(errors).reduce((acc, key) => {
    acc[key] = !!errors[key];
    return acc;
  }, {});

  // Initialize 'dirty' state for each input based on their existing errors.
  // We use this state to optimize the input validation process.
  // At certain points because we only want to validate inputs that have been dirtied.
  // dirty looks like this: { id: true }
  const [dirty, setDirty] = useState(errorBooleans);

  // Helper method to get the error for a given id
  // It takes an id and returns a string used as a error message
  const getError = (id) => {
    return errors?.[id] || null;
  };

  // Helper method to check if the form is valid
  // It only checks if valid or not and does not set any state
  // TODO: If a field depends on another field, we should add that logic here.
  // And at that time isValid would update state. This feature has not been implemented yet or tested yet.
  // Another soltuion would be to handle updating state in the checkFormValidity method.
  const isValid = (formId = pages.START, options = {}) => {
    const { dirtied = false } = options;

    let filteredIds = ids;

    // If dirtied is true, then we only want to validate inputs that have been dirtied.
    // This prevents checking on every input change.
    // Why is this important? Because say we want to validate something that is required
    // but the value is currenlty an empty string. We don't want to show the error message
    // sometimes like if the user navigates back to page 1 and the input is empty.
    if (dirtied) {
      filteredIds = ids.filter((id) => dirty[id]);
    }

    // Finds the first input that has an error from the validationRef object of inputs
    // ------------
    // Check if any input has an error message.
    // The loop will break if an error is found.
    // The variable 'hasValidationError' will be set to true if any input has an error.
    // We return the opposite of 'hasValidationError' to indicate that the form is valid if no errors are found.
    const hasValidationError = filteredIds?.some((id) => {
      if (formId === pages.FORGOTPASS && id === FORM_IDS.PASSWORD) {
        return false;
      }
      const value = validationRef.current[id]?.value;
      const validationError = validationCallback(
        id,
        value,
        currentPageRef,
        pages
      );
      const hasError = hasValue(validationError);
      return hasError;
    });

    // Return opposite of hasValidationError for isValid
    return !hasValidationError;
  };

  // The 'disabled' state controls the 'Submit' button's availability based on form validity.
  const [disabled, setDisabled] = useState(true);

  // Update the 'disabled' state every time the form undergoes changes.
  const checkFormValidity = (event) => {
    if (!event?.target) return;
    event.preventDefault();
    setDisabled(!isValid(currentPageRef.current));
  };

  // This is used under the hood of the handleBlur and handleChange methods.
  const handleInput = (event, globalError = undefined) => {
    if (!event?.target) return;
    event.preventDefault();
    const { id, value } = event.target;
    const validationError = validationCallback(
      id,
      value,
      currentPageRef,
      pages
    );
    actions.setError(id, validationError);
    actions.setGlobalMessage(globalError);
  };

  const handleBlur = useCallback(
    (id) => (event) => {
      handleInput(event);
      // Let's check if we should dirty the input
      // We only want to dirty if the input on blur is valid
      // Guard: first check if dirty, if dirty we bail.
      if (dirty[id]) return;
      // If not dirty, set dirty to true
      setDirty((prevDirty) => ({
        ...prevDirty,
        [id]: true,
      }));
      // In handleInput, we cover valiation for the current input, but
      // we also need to check if the whole form is valid.
      checkFormValidity(event);
    },
    [state.currentPage]
  );

  // Debouncing is applied to improve performance and limit unnecessary validations.
  const handleDebouncedInput = useDebounce(handleInput);
  const handleDebouncedValidation = useDebounce(checkFormValidity);

  // Combines debounced and immediate validation based on the 'dirty' state.
  const handleChange = (id) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCallback(
      (event) => {
        // Only run the debounced input handler if the input is dirty.
        if (dirty[event.target?.id]) {
          handleDebouncedInput(event);
        }
        handleDebouncedValidation(event);
      },
      [dirty[id]]
    );

  return {
    checkFormValidity,
    currentPageRef,
    disabled,
    getError,
    handleBlur,
    handleChange,
    isValid, // should be used to confirm if the whole form is valid
    setDirty,
    validationRef,
  };
};
